/**
 * Utility functions for handling and cleaning error messages
 */

/**
 * Cleans API error messages to make them user-friendly
 * Removes technical error details like "[Chunk X Error: ...]" patterns
 * 
 * @param errorMessage - The raw error message from the API
 * @param defaultMessage - Default message to use if the error is empty after cleaning
 * @returns Cleaned, user-friendly error message
 */
export const cleanApiErrorMessage = (
  errorMessage: string,
  defaultMessage: string = 'An error occurred'
): string => {
  if (!errorMessage || typeof errorMessage !== 'string') {
    return defaultMessage;
  }

  // Check if this is an AI apology/error response
  const aiApologyPatterns = [
    /I apologize.*(?:cannot fulfill your request|cannot perform|cannot complete|am unable to analyze)/i,
    /transcript provided is not actual conversation text/i,
    /transcript content failed to load/i,
    /Please provide the actual transcript/i,
  ];
  
  if (aiApologyPatterns.some(pattern => pattern.test(errorMessage))) {
    return 'Data processing failed due to server error';
  }

  // Pattern to detect error messages like "[Chunk 1 Error: [Error: ...]]" or similar
  const errorPatterns = [
    /\[Chunk\s+\d+\s+Error:\s*\[Error:[^\]]*\]\]/gi,
    /\[Error:[^\]]*\]/gi,
    /Error:\s*module\s+'[^']*'\s+has\s+no\s+attribute\s+'[^']*'/gi,
    /\[Chunk\s+\d+\s+Error:[^\]]*\]/gi,
  ];

  let cleanedMessage = errorMessage;

  // Replace all error patterns with empty string
  errorPatterns.forEach(pattern => {
    cleanedMessage = cleanedMessage.replace(pattern, '');
  });

  // Clean up whitespace
  cleanedMessage = cleanedMessage.trim();

  // If the message is now empty or too short, return the default
  if (cleanedMessage.length < 3) {
    return defaultMessage;
  }

  return cleanedMessage;
};

/**
 * Checks if a string contains API error patterns
 * 
 * @param content - Content to check for errors
 * @returns true if error patterns are found
 */
export const containsApiErrors = (content: string): boolean => {
  if (!content || typeof content !== 'string') {
    return false;
  }

  const errorPatterns = [
    /\[Chunk\s+\d+\s+Error:/i,
    /\[Error:/i,
    /module\s+'[^']*'\s+has\s+no\s+attribute/i,
    /I apologize.*(?:cannot fulfill your request|cannot perform|cannot complete|am unable to analyze)/i,
    /There is no actual transcript data to analyze/i,
    /provided transcript content is an error message/i,
    /transcript content failed to load/i,
    /Please provide the actual transcript/i,
    /not actual conversation text.*error message/i,
  ];

  return errorPatterns.some(pattern => pattern.test(content));
};

/**
 * Cleans content by removing API error messages and returns cleaned content
 * with optional error notification
 * 
 * @param content - The content to clean
 * @param addWarning - Whether to add a warning message if errors were found
 * @returns Object containing cleaned content and whether errors were found
 */
export const cleanContentErrors = (
  content: string,
  addWarning: boolean = true
): { content: string; hasErrors: boolean } => {
  if (!content || typeof content !== 'string') {
    return { content: '', hasErrors: false };
  }

  // Check if content is an AI apology/error response - using multiline approach
  const normalizedContent = content.replace(/\r?\n/g, ' ').replace(/\s+/g, ' ');
  const aiApologyPatterns = [
    /I apologize.*(?:cannot fulfill your request|cannot perform|cannot complete|am unable to analyze)/i,
    /transcript provided is not actual conversation text.*error message/i,
    /transcript content failed to load/i,
    /There is no actual transcript data to analyze/i,
    /Please provide the actual transcript/i,
  ];
  
  if (aiApologyPatterns.some(pattern => pattern.test(normalizedContent))) {
    return {
      content: `⚠️ **Processing Error**\n\nWe encountered an issue while processing this section. The data may be temporarily unavailable or incomplete. Please try refreshing the page or contact support if the issue persists.`,
      hasErrors: true
    };
  }

  const errorPatterns = [
    /\[Chunk\s+\d+\s+Error:\s*\[Error:[^\]]*\]\]/gi,
    /\[Error:[^\]]*\]/gi,
    /Error:\s*module\s+'[^']*'\s+has\s+no\s+attribute\s+'[^']*'/gi,
    /\[Chunk\s+\d+\s+Error:[^\]]*\]/gi,
  ];

  let cleanedContent = content;
  let hasErrors = false;

  // Check if content contains error patterns and remove them
  errorPatterns.forEach(pattern => {
    if (pattern.test(cleanedContent)) {
      hasErrors = true;
      cleanedContent = cleanedContent.replace(pattern, '');
    }
  });

  // Clean up extra whitespace left after removing errors. Collapse runs of
  // blank lines down to a single blank line, but do NOT strip every blank line:
  // markdown needs the blank line before a table/list block for it to render
  // (otherwise GFM tables render as literal "| ... |" text).
  cleanedContent = cleanedContent
    .split('\n')
    .map(line => line.replace(/\s+$/, ''))
    .join('\n')
    .replace(/\n{3,}/g, '\n\n')
    .trim();

  // If content was mostly errors and now empty or very short, return a proper error message
  if (hasErrors && cleanedContent.length < 50) {
    return {
      content: `⚠️ **Processing Error**\n\nWe encountered an issue while processing this section. The data may be temporarily unavailable or incomplete. Please try refreshing the page or contact support if the issue persists.`,
      hasErrors: true
    };
  }

  // If we had errors but still have content, prepend a warning
  if (hasErrors && addWarning && cleanedContent.length >= 50) {
    return {
      content: `⚠️ *Note: Some content may be incomplete due to processing issues.*\n\n${cleanedContent}`,
      hasErrors: true
    };
  }

  return { content: cleanedContent, hasErrors };
};

/**
 * Humanizes network and connection errors to user-friendly messages
 * 
 * @param error - The error object or message
 * @returns Humanized error message
 */
export const humanizeNetworkError = (error: any): string => {
  // Check for network errors (no response from server)
  if (error && typeof error === 'object' && !error.response) {
    // Network request failed
    if (error.code === 'ERR_NETWORK' || error.message?.includes('Network Error')) {
      return 'Unable to connect to the server. Please check your internet connection and try again.';
    }
    
    // Connection timeout
    if (error.code === 'ECONNABORTED' || error.message?.includes('timeout')) {
      return 'The request took too long. Please check your internet connection and try again.';
    }
    
    // DNS/Connection errors
    if (error.code === 'EHOSTUNREACH' || error.message?.includes('EHOSTUNREACH')) {
      return 'Unable to reach the server. Please check your internet connection and try again.';
    }
    
    if (error.code === 'ECONNREFUSED' || error.message?.includes('ECONNREFUSED')) {
      return 'Connection refused. The server may be temporarily unavailable. Please try again later.';
    }
    
    if (error.code === 'ENOTFOUND' || error.message?.includes('ENOTFOUND')) {
      return 'Server not found. Please check your internet connection and try again.';
    }
  }
  
  // Check error message string for technical patterns
  const errorString = typeof error === 'string' 
    ? error.toLowerCase() 
    : (error?.message || error?.error || '').toLowerCase();
  
  if (errorString.includes('ehostunreach') || errorString.includes('connect ehostunreach')) {
    return 'Unable to connect to the server. Please check your internet connection and try again.';
  }
  
  if (errorString.includes('econnrefused') || errorString.includes('connection refused')) {
    return 'Connection refused. The server may be temporarily unavailable. Please try again later.';
  }
  
  if (errorString.includes('timeout') || errorString.includes('timed out')) {
    return 'The request took too long. Please try again.';
  }
  
  if (errorString.includes('network') && errorString.includes('error')) {
    return 'Network error occurred. Please check your internet connection and try again.';
  }
  
  return '';
};

/**
 * Gets a user-friendly error message for display in the UI
 * 
 * @param error - The error object or message
 * @param context - Additional context about where the error occurred
 * @returns User-friendly error message
 */
export const getUserFriendlyErrorMessage = (
  error: any,
  context: string = ''
): string => {
  // First check for network errors
  const networkError = humanizeNetworkError(error);
  if (networkError) {
    return context ? `${context}: ${networkError}` : networkError;
  }

  // If it's already a string, clean it
  if (typeof error === 'string') {
    const cleaned = cleanApiErrorMessage(error);
    return context ? `${context}: ${cleaned}` : cleaned;
  }

  // If it's an error object
  if (error instanceof Error) {
    const cleaned = cleanApiErrorMessage(error.message);
    return context ? `${context}: ${cleaned}` : cleaned;
  }

  // If it has a message property
  if (error && typeof error === 'object' && 'message' in error) {
    const cleaned = cleanApiErrorMessage(String(error.message));
    return context ? `${context}: ${cleaned}` : cleaned;
  }

  // Default fallback
  return context 
    ? `${context}: An unexpected error occurred. Please try again.`
    : 'An unexpected error occurred. Please try again.';
};

